import React, { useState } from "react";
import type { MenuProps } from "antd";
import { Layout, Menu } from "antd";
import { Badge } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { ConfigProvider, Button } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  LogoutOutlined,
  ArrowLeftOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import { useAuth } from "react-oidc-context";
import { useHasRole } from "../lib/helpers/roles";
import "dayjs/locale/nl";
import dayjs from "dayjs";
import locale from "antd/locale/nl_NL";

const { Header, Content, Sider } = Layout;

import { dashboardMenuConfig } from "./dashboardMenuConfig";
import { adminTheme } from "../theme/adminTheme";
import { AffiliateSwitcher } from "./AffiliateSwitcher";
import { useCurrentUser } from "../lib/hooks/useCurrentUser";
import { useChatUnhandledCount } from "../lib/hooks/useChat";
import { Grid } from "antd";
import { validate } from "uuid";

const { useBreakpoint } = Grid;

interface CustomTriggerProps {
  collapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
  reverseArrow?: boolean;
  direction?: "ltr" | "rtl";
}

dayjs.locale("nl");

export const DashboardLayout: React.FC = () => {
  const hasAdminRole = useHasRole("ROLE_OIDC_ADMIN");
  const currentUser = useCurrentUser();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const screens = useBreakpoint();
  const isTabletOrLarger = screens.md;

  // 992px or larger: sidebar is always visible. Collapsed is 80px wide;
  // Between 768px and 992px: sidebar is collapsed by default, but can be expanded;
  // Below 768px: sidebar is hidden by default and trigger toggles visibility.

  const { activeItem, menuItems, selectedKeys, openKeys } = React.useMemo(
    () =>
      dashboardMenuConfig({
        pathname: location.pathname,
        hasAdminRole,
        currentUser,
      }),
    [location.pathname, hasAdminRole, currentUser],
  );

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    navigate(`/${e.key}`);
  };

  const hasChatNav = menuItems.some((item) => item?.key === "chat");
  const unhandledCountQuery = useChatUnhandledCount(hasChatNav);
  const unhandledCount = unhandledCountQuery.data ?? 0;

  const decoratedMenuItems = React.useMemo(
    () =>
      menuItems.map((item) =>
        item?.key === "chat"
          ? {
              ...item,
              label: (
                <Badge count={unhandledCount} size="small" offset={[12, 0]}>
                  <span>Berichten</span>
                </Badge>
              ),
            }
          : item,
      ),
    [menuItems, unhandledCount],
  );

  const isDetailPage = validate(location.pathname.split("/").pop() || "");

  const isBuilderPage = location.pathname.includes(
    "form-configurations/builder",
  );

  const isDashboardHome = location.pathname === "/dashboard";
  const headerIcon =
    activeItem?.icon ?? (isDashboardHome ? <DashboardOutlined /> : null);
  const headerTitle = activeItem?.label ?? (isDashboardHome ? "Dashboard" : "");

  return (
    <ConfigProvider theme={adminTheme} locale={locale}>
      <Layout className="h-[100vh] font-[Inter]">
        <Sider
          collapsed={collapsed}
          width={300}
          breakpoint="lg"
          collapsedWidth={isTabletOrLarger ? 80 : 0}
          onCollapse={(value) => setCollapsed(value)}
          className="bg-white flex flex-col"
        >
          <div className="flex flex-col h-full">
            <a
              className="px-4 py-2 border-neutral-300 border-b h-[90px] flex justify-center items-center"
              href="/"
            >
              <img
                width={0}
                height={0}
                alt="RumbleDirect Logo"
                src={`${getConfigValue("cdnUrl")}/static/assets/logos/logo-rumble.svg`}
                className="max-h-[70%] max-w-[70%] w-auto h-auto"
              />
            </a>

            <div className="h-[calc(100vh-190px)] overflow-auto">
              <Menu
                selectedKeys={selectedKeys}
                defaultOpenKeys={openKeys}
                items={decoratedMenuItems}
                className="px-3 mt-3"
                onClick={handleMenuClick}
                mode="inline"
                styles={{
                  subMenu: {
                    item: {
                      color: "#555",
                      height: 35,
                      fontSize: 13,
                      paddingLeft: 16,
                      width: "calc(100% - 36px)",
                      marginLeft: 36,
                    },
                  },
                }}
              />
            </div>

            <CustomTrigger
              collapsed={collapsed}
              onToggle={() => setCollapsed(!collapsed)}
              onLogout={auth.signoutRedirect}
              reverseArrow={false}
              direction="ltr"
            />
          </div>
        </Sider>
        <Layout className="border-l border-neutral-300">
          <Header
            className={`bg-white border-neutral-300 border-b h-[90px] p-9 items-center gap-5 ${isBuilderPage ? "hidden" : "flex"}`}
          >
            {isDetailPage ? (
              <div
                className="flex items-center justify-center w-8 h-8 bg-black text-white rounded-lg p-2 text-lg cursor-pointer"
                onClick={() => navigate(-1)}
              >
                <ArrowLeftOutlined />
              </div>
            ) : headerIcon ? (
              <div className="flex items-center justify-center w-8 h-8 bg-black text-white rounded-lg p-2 text-lg">
                {headerIcon}
              </div>
            ) : null}

            <h1 className="text-2xl font-bold">{headerTitle}</h1>
          </Header>

          <Content
            className={`h-[calc(100vh-90px)] overflow-y-scroll ${isBuilderPage ? "" : "px-9 py-6"}`}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

const CustomTrigger: React.FC<CustomTriggerProps> = ({
  collapsed,
  onToggle,
  onLogout,
  reverseArrow = false,
  direction = "ltr",
}) => {
  const reverseIcon = (direction === "rtl") === !reverseArrow;

  const iconObj = {
    expanded: reverseIcon ? (
      <RightOutlined className="!text-[17px]" />
    ) : (
      <LeftOutlined className="!text-[17px]" />
    ),
    collapsed: reverseIcon ? (
      <LeftOutlined className="!text-[17px]" />
    ) : (
      <RightOutlined className="!text-[17px]" />
    ),
  };

  const status = collapsed ? "collapsed" : "expanded";
  const collapseIcon = iconObj[status];
  const isAdmin = useHasRole("ROLE_OIDC_ADMIN");

  return (
    <div className="mt-auto w-full px-2">
      <div className="w-full mb-2">{isAdmin && <AffiliateSwitcher />}</div>
      <div className="flex items-center justify-center border-t border-neutral-200 py-2 gap-2">
        <Button
          type="text"
          icon={collapseIcon}
          onClick={onToggle}
          className="!h-8 !w-8 leading-[1]"
          title={collapsed ? "Uitklappen" : "Inklappen"}
        />
        <Button
          type="text"
          icon={<LogoutOutlined className="!text-[17px]" />}
          onClick={onLogout}
          className="!h-8 !w-8 leading-[1]"
          title="Uitloggen"
        />
      </div>
    </div>
  );
};
